// Feature #287 — Staleness / Settings admin (sm-admin). Key/value edits
// against sm_setting; staleness evaluation reads values live, so changes
// apply from the next sweep without redeploy.
import React, { useEffect, useState } from 'react'
import { PageHeader } from '@/components/ui/PageHeader'
import { formatDateTime } from '@/utils/dateFormat'
import { getSmSettings, SmSetting, updateSmSetting } from '@/api/sm'

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const SmSettings: React.FC = () => {
  const [settings, setSettings] = useState<SmSetting[]>([])
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const load = () => {
    getSmSettings()
      .then(setSettings)
      .catch((e) => setError(e?.message || 'Failed to load settings'))
  }

  useEffect(load, [])

  const handleSave = async (setting: SmSetting) => {
    setError(null)
    setSaved(null)
    try {
      await updateSmSetting(setting.key, edits[setting.key] ?? setting.value)
      setSaved(setting.key)
      setEdits((prev) => {
        const next = { ...prev }
        delete next[setting.key]
        return next
      })
      load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to save setting')
    }
  }

  return (
    <div>
      <PageHeader title="Skills Management — Staleness / Settings" />
      <div style={{ padding: '24px', maxWidth: '860px', display: 'grid', gap: '16px' }}>
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#b91c1c',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}
        <p style={{ fontSize: '13px', color: 'var(--color-dark-grey)', margin: 0 }}>
          Staleness is measured per skill per person against the last assessment date. The
          daily sweep (07:15 Hobart, weekdays) prompts anyone whose skills exceed{' '}
          <code>staleness_days</code>, no more than once per <code>staleness_reprompt_days</code>.
        </p>
        {settings.map((s) => (
          <div
            key={s.key}
            style={{
              border: '1px solid var(--color-light-grey)',
              borderRadius: '8px',
              padding: '16px',
              display: 'grid',
              gap: '8px',
            }}
          >
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <strong style={{ minWidth: '220px', fontFamily: 'monospace', fontSize: '14px' }}>
                {s.key}
              </strong>
              <input
                style={{ ...inputStyle, width: '140px' }}
                value={edits[s.key] ?? s.value}
                onChange={(e) => setEdits((prev) => ({ ...prev, [s.key]: e.target.value }))}
              />
              <button
                onClick={() => handleSave(s)}
                disabled={edits[s.key] === undefined || edits[s.key] === s.value}
                style={{
                  padding: '6px 14px',
                  backgroundColor: 'var(--color-magenta)',
                  color: 'var(--color-white)',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                  opacity: edits[s.key] === undefined || edits[s.key] === s.value ? 0.5 : 1,
                }}
              >
                Save
              </button>
              {saved === s.key && (
                <span style={{ color: '#16a34a', fontSize: '13px' }}>Saved</span>
              )}
            </div>
            {s.description && (
              <p style={{ fontSize: '13px', color: 'var(--color-dark-grey)', margin: 0 }}>
                {s.description}
              </p>
            )}
            <p style={{ fontSize: '12px', color: 'var(--color-dark-grey)', margin: 0 }}>
              Last updated {formatDateTime(s.updated_at)}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

export default SmSettings
